import type { ZoneItem } from "@/mocks/home";

interface ZoneSectionProps {
  id: string;
  index: string;
  title: string;
  subtitle: string;
  items: ZoneItem[];
  tone: "primary" | "secondary";
  surface?: "plain" | "tint";
}

export default function ZoneSection({
  id,
  index,
  title,
  subtitle,
  items,
  tone,
  surface = "plain",
}: ZoneSectionProps) {
  const accentText = tone === "primary" ? "text-primary-600" : "text-secondary-600";
  const accentBar = tone === "primary" ? "bg-primary-500" : "bg-secondary-500";
  const chip = tone === "primary" ? "bg-primary-100 text-primary-800" : "bg-secondary-100 text-secondary-900";

  return (
    <section id={id} className={surface === "tint" ? "bg-background-100" : "bg-background-50"}>
      <div className="mx-auto max-w-[1180px] px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 pb-6 border-b border-background-300">
          <div className="flex items-start gap-4 md:gap-6">
            <span className={`font-heading text-3xl md:text-4xl font-bold leading-none ${accentText} tabular-nums`}>
              {index}
            </span>
            <div>
              <span className={`inline-block w-8 h-1 rounded-full mb-3 ${accentBar}`}></span>
              <h2 className="font-heading text-2xl md:text-[30px] font-bold text-foreground-950 leading-tight">
                {title}
              </h2>
              <p className="mt-2 text-sm text-foreground-500 max-w-xl">{subtitle}</p>
            </div>
          </div>
          <a href="#today" className="text-xs text-foreground-600 hover:text-primary-600 flex items-center gap-1 whitespace-nowrap">
            进入分区
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-0 mt-2">
          {items.map((item, idx) => (
            <a
              key={item.title}
              href="#today"
              className="group flex gap-4 py-5 border-b border-background-200"
            >
              <span className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-xs font-label ${chip}`}>
                {String(idx + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-[15px] font-medium leading-6 text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-foreground-600 line-clamp-2">{item.summary}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-foreground-400">
                  <i className="ri-line-chart-line" aria-hidden="true"></i>
                  {item.heat}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}